<template>
  <div :class="['dropdown', { error: error, 'not-empty': notEmpty, open: isOpened }]">
    <modal-header-white
      v-if="isMobile && isOpened"
      :device-type="'mobile'"
      :label="mobileTitle"
      @closed="close"
    />
    <multiselect
      :value="selectedItem"
      :options="items"
      :searchable="false"
      track-by="value"
      label="title"
      :close-on-select="closeOnSelect || !isMobile"
      :placeholder="''"
      :allow-empty="false"
      :options-limit="100"
      :show-no-results="false"
      :show-no-options="false"
      :show-labels="true"
      :max-height="275"
      open-direction="bottom"
      select-label=""
      selected-label=""
      deselect-label=""
      :clear-on-select="false"
      :preserve-search="false"
      @open="setOpened"
      @close="setClosed"
      @select="onChange"
      @search-change="onSearch"
    >
      <template #caret="{ toggle }">
        <label class="label">{{ label }}</label>
        <span
          class="arrow"
          @mousedown.prevent.stop="toggle"
        />
      </template>

      <template
        slot="singleLabel"
        slot-scope="{ option }"
      >
        <div class="selected">{{ option.title }}</div>
      </template>

      <template
        slot="option"
        slot-scope="props"
      >
        <div class="option__title">{{ props.option.title }}</div>
      </template>
    </multiselect>
    <div
      v-if="error"
      class="error-msg"
    >
      {{ errorMsg }}
    </div>
  </div>
</template>

<script>
  import { mapGetters } from 'vuex'

  export default {
    components: {
      Multiselect: () =>
        import(/* webpackChunkName: "vue-multiselect" */ 'vue-multiselect'),
      ModalHeaderWhite: () =>
        import(
          /* webpackChunkName: "modal-header-white" */ 'components/Modal/ModalHeaderWhite'
        )
    },
    props: {
      error: {
        type: Boolean,
        required: false,
        default: false
      },
      errorMsg: {
        type: String,
        required: false,
        default: ''
      },
      label: {
        type: String,
        default: '',
        required: false
      },
      items: {
        type: Array,
        default: () => []
      },
      selectedItem: {
        type: Object,
        default: () => {}
      },
      mobileTitle: {
        type: String,
        default: '',
        required: false
      },
      splitTitle: {
        type: Boolean,
        default: false
      },
      ignoreListChangeEvent: {
        type: Boolean,
        default: false
      },
      closeOnSelect: {
        type: Boolean,
        default: false
      }
    },
    data() {
      return {
        isOpened: false
      }
    },
    computed: {
      ...mapGetters('device', ['isMobile']),
      notEmpty() {
        return Object.keys(this.selectedItem).length > 0
      }
    },
    methods: {
      setOpened() {
        this.isOpened = true
      },
      setClosed() {
        this.isOpened = false
      },
      onChange(value) {
        this.$emit('change', value)
      },
      onSearch(value) {
        this.$emit('search', value)
      },
      close() {
        this.$emit('close')
      }
    }
  }
</script>

<style
  lang="scss"
  scoped
  rel="stylesheet/scss"
>
  @import '~assets/styles/common.scss';
  @import '~assets/styles/vue-multiselect.css';

  .dropdown {
    &.open {
      @include bp-mobile {
        display: block;
        width: 100%;
        position: fixed;
        background: white;
        height: calc(100vh - 55px);
        top: 55px;
        left: 0;
        z-index: 4;
        overflow-y: scroll;

        .arrow {
          display: none;
        }

        /deep/ {
          .multiselect__tags {
            display: none;
          }
        }
      }
    }

    &.error {
      margin-bottom: 16px;

      .multiselect {
        margin-bottom: 12px;
      }

      /deep/ {
        .multiselect__tags {
          border-color: $red-dark;
        }
      }

      .error-msg {
        display: block;
      }
    }

    &.not-empty {
      .multiselect {
        .label {
          position: absolute;
          transition: 0.2s ease all;
          @include text-overflow();
          pointer-events: none;
          color: $blue-light;
          top: 7px;
          font-size: em(10);
          letter-spacing: 0;
          line-height: 14px;
        }
      }
    }
  }

  .multiselect,
  .multiselect__input,
  .multiselect__single {
    min-height: 56px;
    margin-bottom: 16px;
    z-index: 1 !important;

    .label {
      position: absolute;
      top: calc(50% - 10px);
      left: 16px;
      transition: 0.2s ease all;
      @include text-overflow();
      line-height: 20px;
      pointer-events: none;
      color: $blue-light;
    }

    &--active {
      /deep/ {
        input {
          top: 5px;
          margin: 0;
          font-size: em(14);
          background: transparent;
        }

        .multiselect__tags {
          border: 1px solid $blue-medium !important;
          border-radius: 4px;

          input {
            height: 24px;
            line-height: 24px;
            padding: 0;
          }
        }
      }

      .arrow {
        transform: rotate(-90deg);
      }
    }

    /deep/ {
      .multiselect__tags {
        min-height: 56px;
        padding: 16px 60px 6px 16px;
        border: 1px solid $blue-border;
        font-size: em(14);
        line-height: 20px;
        color: $blue-light;
        border-radius: 4px;
      }

      .multiselect__content-wrapper {
        @include bp-mobile {
          top: 0;
          max-height: unset !important;
        }

        @include bp-mf-tablet {
          top: 65px;
          z-index: 1 !important;
          @include box-shadow();
        }
      }

      .multiselect__placeholder {
        font-size: em(14);
        color: $blue-light;
        margin: 0;
      }

      .multiselect__single {
        color: $blue-dark;
        background: transparent;

        .label {
          position: absolute;
          width: 100%;
          transition: 0.2s ease all;
          @include text-overflow();
          pointer-events: none;
          color: $blue-light;
          top: -10px;
          left: 0;
          font-size: em(10);
          letter-spacing: 0;
          line-height: 14px;
        }

        .selected {
          position: relative;
          top: 8px;
          left: -5px;
        }
      }

      .multiselect__option {
        height: 55px;
        padding: 14px 16px;
        font-size: em(14);
        letter-spacing: 0;
        line-height: 27px;

        &--selected,
        &--highlight {
          background-color: $gray-marble;
          color: $dropdown-selected;
          font-weight: 500;
        }
      }
    }
  }

  .arrow {
    position: absolute;
    right: 19px;
    transform: rotate(90deg);
    line-height: 16px;
    top: calc(50% - 20px);
    display: block;
    width: 40px;
    height: 40px;

    &::after {
      content: $icon-arrow-right-thin;
      @include icon-font-thin();
      font-size: 16px;
      color: $blue-dark;
      position: absolute;
      left: calc(50% - 5px);
      top: calc(50% - 10px);
    }
  }
</style>
